export type LiverDirectoryStatus = 'active' | 'inactive' | 'graduated'
export type LiverDirectoryFilterStatus = 'all' | LiverDirectoryStatus

export interface LiverDirectoryLiver {
  id: string
  display_name: string
  line_user_id: string | null
  status: LiverDirectoryStatus
  joined_at: string | null
  left_at: string | null
  notes: string | null
}

export interface LiverDirectoryRow {
  id: string
  displayName: string
  lineUserId: string | null
  status: LiverDirectoryStatus
  joinedAt: string | null
  leftAt: string | null
  hasLineId: boolean
  notePreview: string
  currentManagerId: string | null
  currentManagerName: string
  currentManagerDepartment: string | null
  isMine: boolean
  hasCallHistory: boolean
  latestCallAt: string | null
  hasLineHistory: boolean
  latestLineAt: string | null
}

export interface LiverDirectory {
  counts: Record<'all' | LiverDirectoryStatus, number>
  myAssignedCount: number
  rows: LiverDirectoryRow[]
}

export type LiverDirectoryAssignmentView = 'all' | 'mine'

export interface LiverDirectoryAssignment {
  liver_id: string
  manager_id: string
  ended_at: string | null
  started_at?: string | null
  manager?: {
    name: string | null
    email: string
    department: string | null
  } | null
}

export interface LiverDirectoryActivity {
  liver_id: string
  source: 'call' | 'line'
  occurred_at: string | null
}

export interface LiverDirectoryOptions {
  status?: LiverDirectoryFilterStatus
  searchText?: string
  assignmentView?: LiverDirectoryAssignmentView
  currentManagerId?: string | null
  assignments?: LiverDirectoryAssignment[]
  activities?: LiverDirectoryActivity[]
}

const STATUS_ORDER: Record<LiverDirectoryStatus, number> = {
  active: 0,
  inactive: 1,
  graduated: 2
}

export function buildLiverDirectory(
  livers: LiverDirectoryLiver[],
  options: LiverDirectoryOptions = {}
): LiverDirectory {
  const assignmentByLiver = buildCurrentAssignmentMap(options.assignments ?? [])
  const activityByLiver = buildActivityMap(options.activities ?? [])
  const counts = {
    all: livers.length,
    active: livers.filter((liver) => liver.status === 'active').length,
    inactive: livers.filter((liver) => liver.status === 'inactive').length,
    graduated: livers.filter((liver) => liver.status === 'graduated').length
  }
  const normalizedSearch = options.searchText?.trim().toLowerCase() ?? ''
  const status = options.status ?? 'all'
  const currentManagerId = options.currentManagerId ?? null
  const assignmentView = options.assignmentView ?? 'all'
  const myAssignedCount = livers.filter((liver) => {
    const assignment = assignmentByLiver.get(liver.id)
    return Boolean(currentManagerId && assignment?.manager_id === currentManagerId)
  }).length

  const rows = livers
    .filter((liver) => status === 'all' || liver.status === status)
    .map((liver) => toRow(liver, {
      assignment: assignmentByLiver.get(liver.id) ?? null,
      activity: activityByLiver.get(liver.id) ?? null,
      currentManagerId
    }))
    .filter((row) => assignmentView === 'all' || row.isMine)
    .filter((row) => matchesSearch(row, normalizedSearch))
    .sort(compareRows)

  return { counts, myAssignedCount, rows }
}

function toRow(
  liver: LiverDirectoryLiver,
  context: {
    assignment: LiverDirectoryAssignment | null
    activity: ActivitySummary | null
    currentManagerId: string | null
  }
): LiverDirectoryRow {
  const managerName = readableManagerName(context.assignment)
  const latestCallAt = context.activity?.latestCallAt ?? null
  const latestLineAt = context.activity?.latestLineAt ?? null

  return {
    id: liver.id,
    displayName: liver.display_name,
    lineUserId: liver.line_user_id,
    status: liver.status,
    joinedAt: liver.joined_at,
    leftAt: liver.left_at,
    hasLineId: Boolean(liver.line_user_id?.trim()),
    notePreview: preview(liver.notes),
    currentManagerId: context.assignment?.manager_id ?? null,
    currentManagerName: managerName,
    currentManagerDepartment: context.assignment?.manager?.department ?? null,
    isMine: Boolean(context.currentManagerId && context.assignment?.manager_id === context.currentManagerId),
    hasCallHistory: Boolean(latestCallAt),
    latestCallAt,
    hasLineHistory: Boolean(latestLineAt),
    latestLineAt
  }
}

function matchesSearch(row: LiverDirectoryRow, searchText: string): boolean {
  if (!searchText) return true
  return [
    row.displayName,
    row.lineUserId ?? '',
    row.notePreview,
    row.currentManagerName,
    row.currentManagerDepartment ?? ''
  ].some((value) => value.toLowerCase().includes(searchText))
}

function compareRows(a: LiverDirectoryRow, b: LiverDirectoryRow): number {
  return STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
    a.displayName.localeCompare(b.displayName, 'ja') ||
    a.id.localeCompare(b.id)
}

function preview(value: string | null): string {
  const text = value?.replace(/\s+/g, ' ').trim() ?? ''
  if (!text) return 'メモなし'
  return text.length > 48 ? `${text.slice(0, 47)}…` : text
}

type ActivitySummary = {
  latestCallAt: string | null
  latestLineAt: string | null
}

function buildCurrentAssignmentMap(assignments: LiverDirectoryAssignment[]): Map<string, LiverDirectoryAssignment> {
  const current = assignments
    .filter((assignment) => assignment.ended_at === null)
    .sort(compareAssignmentsDesc)
  const result = new Map<string, LiverDirectoryAssignment>()
  for (const assignment of current) {
    if (!result.has(assignment.liver_id)) {
      result.set(assignment.liver_id, assignment)
    }
  }
  return result
}

function buildActivityMap(activities: LiverDirectoryActivity[]): Map<string, ActivitySummary> {
  const result = new Map<string, ActivitySummary>()
  for (const activity of activities) {
    const summary = result.get(activity.liver_id) ?? { latestCallAt: null, latestLineAt: null }
    if (activity.source === 'call') {
      summary.latestCallAt = maxTimestamp(summary.latestCallAt, activity.occurred_at)
    } else {
      summary.latestLineAt = maxTimestamp(summary.latestLineAt, activity.occurred_at)
    }
    result.set(activity.liver_id, summary)
  }
  return result
}

function readableManagerName(assignment: LiverDirectoryAssignment | null): string {
  return assignment?.manager?.name?.trim() || assignment?.manager?.email || '未設定'
}

function compareAssignmentsDesc(a: LiverDirectoryAssignment, b: LiverDirectoryAssignment): number {
  return compareNullableTimestampDesc(a.started_at ?? null, b.started_at ?? null)
}

function maxTimestamp(current: string | null, next: string | null): string | null {
  if (!current) return next
  if (!next) return current
  return new Date(next).getTime() > new Date(current).getTime() ? next : current
}

function compareNullableTimestampDesc(a: string | null, b: string | null): number {
  if (a && b) return new Date(b).getTime() - new Date(a).getTime()
  if (a) return -1
  if (b) return 1
  return 0
}
